import React, { useState } from 'react';
import { Download } from 'lucide-react';
import ticketApi from '../../pages/JIFI/ticket-dashboard/services/ticketApi';
import Papa from 'papaparse';
import { format } from 'date-fns';
import { getLabel } from '@pages/JIFI/warranty-claim';
import { FAULT_REASONS } from '@pages/JIFI/warranty-claim/warranty-claim';

import { joinMonogramValues } from '../../pages/JIFI/ticket-export';

const ExportRecentButton = ({ ticketType }) => {
  const [isExporting, setIsExporting] = useState(false);

  const getTicketTypeParam = (type) => {
    switch (type) {
      case 'warranty':
        return 'WARRANTY';
      case 'order':
        return 'ORDER';
      case 'repair':
        return 'REPAIR';
      default:
        return 'WARRANTY';
    }
  };

  const flattenTicketData = (tickets) => {
    const flattenedData = [];

    tickets.forEach(ticket => {
      const baseData = {
        'Ticket Type': ticket.ticketType,
        'Customer Name': ticket.customerName,
        'Batch Code': ticket.batchCode,
        'Staff Name': ticket.staffName,
        'Order Number': ticket.orderNumber,
        'Store Name': ticket.storeName,
        'Closed Date': ticket.closedAt,
        'Status': ticket.status?.originalStatus,
      };

      if (ticket.ticketType === 'ORDER') {
        flattenedData.push({
          ...baseData,
          'Action Description': ticket.details?.actionDescription,
          'Day Needed By': ticket.details?.dayNeededBy,
          'To Be Shipped': ticket.details?.toBeShipped ? 'Yes' : 'No',
          'Department': ticket.details?.department,
          'Attachments': ticket.details?.attachments?.join('\n ') || ''
        });
      } else if (ticket.ticketType === 'WARRANTY') {
        if (ticket.details?.replacementSkus && ticket.details.replacementSkus.length > 0) {
          ticket.details.replacementSkus.forEach((replacementSku, index) => {
            flattenedData.push({
              ...baseData,
              'Fault Reason': getLabel(FAULT_REASONS, ticket.details?.faultReason),
              'Customer Request': ticket.details?.customerRequest,
              'Is Fulfilled': ticket.details?.isFulfilled ? 'Yes' : 'No',
              'Attachments': ticket.details?.attachments?.join('\n ') || '',
              'Replacement Item #': index + 1,
              'Original SKU': replacementSku.originalSku,
              'Replacement SKU': replacementSku.replacementSku,
              'Monogram Text': joinMonogramValues(replacementSku.monogram, 'text'),
              'Monogram Font': joinMonogramValues(replacementSku.monogram, 'font'),
              'Monogram Color': joinMonogramValues(replacementSku.monogram, 'color'),
              'Monogram Location': joinMonogramValues(replacementSku.monogram, 'location'),
              'Replacement To Be Shipped': replacementSku.toBeShipped ? 'Yes' : 'No',
              'Replacement Date Needed By': replacementSku.dateNeededBy
            });
          });
        } else {
          flattenedData.push({
            ...baseData,
            'Fault Reason': getLabel(FAULT_REASONS, ticket.details?.faultReason),
            'Customer Request': ticket.details?.customerRequest,
            'Is Fulfilled': ticket.details?.isFulfilled ? 'Yes' : 'No',
            'Attachments': ticket.details?.attachments?.join('\n ') || ''
          });
        }
      } else if (ticket.ticketType === 'REPAIR') {
        if (ticket.details?.itemRepairs && ticket.details.itemRepairs.length > 0) {
          ticket.details.itemRepairs.forEach((itemRepair) => {
            if (itemRepair.parts && itemRepair.parts.length > 0) {
              itemRepair.parts.forEach((part, partIndex) => {
                flattenedData.push({
                  ...baseData,
                  'Is Completed': ticket.details?.isCompleted ? 'Yes' : 'No',
                  'Additional Notes': ticket.details?.additionalNotes,
                  'Attachments': ticket.details?.attachments?.join('\n ') || '',
                  'Repair Item SKU': itemRepair.sku,
                  'Repair Types': itemRepair.repairTypes?.join('\n ') || '',
                  'Repair To Be Shipped': itemRepair.toBeShipped ? 'Yes' : 'No',
                  'Repair Date Needed By': itemRepair.dateNeededBy,
                  'Part #': partIndex + 1,
                  'Part Name': part.name,
                  'Part Quantity': part.quantity
                });
              });
            } else {
              flattenedData.push({
                ...baseData,
                'Is Completed': ticket.details?.isCompleted ? 'Yes' : 'No',
                'Additional Notes': ticket.details?.additionalNotes,
                'Attachments': ticket.details?.attachments?.join('\n ') || '',
                'Repair Item SKU': itemRepair.sku,
                'Repair Types': itemRepair.repairTypes?.join('\n ') || '',
                'Repair To Be Shipped': itemRepair.toBeShipped ? 'Yes' : 'No',
                'Repair Date Needed By': itemRepair.dateNeededBy
              });
            }
          });
        } else {
          flattenedData.push({
            ...baseData,
            'Is Completed': ticket.details?.isCompleted ? 'Yes' : 'No',
            'Additional Notes': ticket.details?.additionalNotes,
            'Attachments': ticket.details?.attachments?.join('\n ') || ''
          });
        }
      } else {
        flattenedData.push(baseData);
      }
    });

    return flattenedData;
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);

      const params = {
        type: getTicketTypeParam(ticketType),
        open: false,  // Only closed tickets
        limit: 10,    // 10 recent tickets
        page: 1
      };

      const tickets = await ticketApi.exportTickets(params);

      if (!tickets || tickets.length === 0) {
        console.warn('No closed tickets found for export');
        return;
      }

      const flattenedData = flattenTicketData(tickets);
      const csv = Papa.unparse(flattenedData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `${ticketType}-recent-export-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className={`
        inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-black rounded-md transition-all duration-200 mb-4
        ${isExporting 
          ? 'opacity-50 cursor-not-allowed' 
          : 'hover:bg-gray-800 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
        }
      `}
    >
      {isExporting ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
          Exporting...
        </>
      ) : (
        <>
          <Download className="w-4 h-4 mr-2" />
          Export Closed Recent
        </>
      )}
    </button>
  );
};

export default ExportRecentButton;
